import "./GlobalAudioPlayer.scss";
import { daw, useDAW, useDAWTrack } from "@highnote/daw/src";
import React, { useEffect, useMemo } from "react";
import classNames from "classnames";
import { SeekableLineform } from "App/common/AudioPlayer";
import { useTheme } from "App/common/ThemeProvider";
import { AudioPlayToggle } from "App/common/AudioPlayer/AudioPlayToggle";
import { ReactComponent as PlayFillSVG } from "App/common/icons-v2/play-fill.svg";
import { ReactComponent as PauseFillSVG } from "App/common/icons-v2/pause-fill-1.svg";
import {
  getDAWTrackId,
  getPersistedAudioQuality,
  useGlobalAudioPlayer,
  useHTMLAudioPlayer,
  QueueableTrack,
} from ".";
import { VolumeControl } from "../VolumeControl";
import {
  formatDuration,
  hasRole,
  isFilePlayable,
  isSpaceAudioQualityLocked,
  spaceHasRolesCheck,
  asSeconds,
} from "@highnote/server/src/core/shared-util";
import {
  ThumbnailPreview,
  useTrackArtworkUrl,
} from "App/common/ThumbnailPreview";
import { AUDIO_QUALITY, ROLE, Track } from "@highnote/server/src/core/entities";
import { TrackDescription, TrackTitle } from "../EntityTable/EntityPreview";
import { AudioProcessingIcon } from "../AudioProcessingIcon";
import { ToggleAudioQuality } from "./ToggleAudioQuality";
import { TrackEditorProvider } from "App/components/useTrackEditor";
import { getAuthId, useAuth } from "../Auth";
import { highnote } from "@highnote/server/src/sdk";
import { BaseMediaControls } from "./BaseMediaControls";
import { useSpaceRaw } from "../useEntities";
import { StatusIndicator } from "App/common/StatusIndicator";
import { useLocation } from "react-router";
import { Button, BUTTON_SIZE, BUTTON_THEME } from "App/core/Button";
import { useViewport } from "App/common/useViewport";
import { CustomIntercomLauncher } from "../CustomIntercomLauncher/CustomIntercomLauncher";
import { useTrackVersionEditorContext } from "../TrackVersionEditor/utils/context/TrackVersionEditorContext";
import { useUser } from "../useEntities/useUser";
import { useGlobalSpaces } from "App/store/spaces/useGlobalSpaces";

const GlobalAudioTrackPreviewUI = ({
  track,
  onClick,
  children,
}: {
  // A QUEUED track: it may carry the space it was played FROM.
  track: QueueableTrack;
  onClick: () => void;
  children?: React.ReactNode;
}) => {
  const { user } = useUser();
  const { source, fallback } = useTrackArtworkUrl({ track });
  const { openTrackVersionEditor } = useTrackVersionEditorContext();
  const { globalSpaces, manageGlobalSingleSpaceWatcher } = useGlobalSpaces();
  const space = globalSpaces.spaces.get(track?.spaceId);
  const spaceHasUploadPermissions = spaceHasRolesCheck(space, [ROLE.UPLOAD]);
  const isUserOwner = space?.createdBy === user?.id;
  const userHasUploadPermissions =
    isUserOwner || hasRole(user?.id, ROLE.UPLOAD, space);
  const userCanUploadTrackVersion =
    spaceHasUploadPermissions || userHasUploadPermissions;
  const userHasManagePermissions =
    isUserOwner || hasRole(user?.id, ROLE.MANAGE, space);

  useEffect(() => {
    if (!space && track?.spaceId) {
      manageGlobalSingleSpaceWatcher({
        action: "attach",
        componentId: "space-global-audio-player",
        spaceId: track.spaceId,
      });

      return () => {
        manageGlobalSingleSpaceWatcher({
          action: "detach",
          componentId: "space-global-audio-player",
          spaceId: track.spaceId,
        });
      };
    }
  }, [track?.spaceId]);

  if (!track) return null;

  return (
    <>
      <ThumbnailPreview
        className="global-audio-player-track-preview"
        title={
          <TrackTitle
            onClick={userHasManagePermissions ? onClick : undefined}
            track={track}
          />
        }
        description={
          children || (
            <TrackDescription
              onClick={
                userCanUploadTrackVersion
                  ? () =>
                      openTrackVersionEditor({
                        track,
                        // The queue also carries whether the track is
                        // borrowed, for exactly this: the editor outlives
                        // the route, so it cannot work the answer out from
                        // the URL. Absent on a plain canonical `Track`,
                        // which is never borrowed for this viewer.
                        isBorrowed: track.isBorrowed === true,
                        type: "default",
                        // The queue carries the space the track was played
                        // FROM; this editor outlives the route.
                        viewingSpaceId: track.viewingSpaceId,
                      })
                  : undefined
              }
              track={track}
              showTrackTime
            />
          )
        }
        iconFallback={fallback}
        iconSource={source}
      />
    </>
  );
};

export const GlobalAudioTrackPreview = ({
  track,
  onClick,
  children,
}: {
  track: Track;
  onClick: () => void;
  children?: React.ReactNode;
}) => {
  return (
    <TrackEditorProvider>
      <GlobalAudioTrackPreviewUI track={track} onClick={onClick}>
        {children}
      </GlobalAudioTrackPreviewUI>
    </TrackEditorProvider>
  );
};

const GlobalAudioSeeker = () => {
  const { nowPlaying, seek } = useGlobalAudioPlayer();
  const { isActive, currentDuration, currentTime, onSeek, useHTMLPlayer } =
    useHTMLAudioPlayer();
  const dawTrack = useDAWTrack(nowPlaying.id);

  const handleSeek = (ts: number) => {
    isActive ? onSeek(ts) : seek(ts + (dawTrack?.startTime || 0));
  };

  return (
    <SeekableLineform
      currentTime={
        useHTMLPlayer ? currentTime : daw.getTrackTime(nowPlaying.id)
      }
      duration={
        (useHTMLPlayer
          ? currentDuration
          : nowPlaying?.file?.metadata?.duration || dawTrack?.duration) || 0
      }
      onSeek={handleSeek}
    />
  );
};

export const GlobalAudioCurrentTime = () => {
  useDAW(["currentTime"]);
  const { nowPlaying } = useGlobalAudioPlayer();
  const { useHTMLPlayer, currentTime: HTMLPlayerCurrentTime } =
    useHTMLAudioPlayer();
  const dawTrackId = getDAWTrackId(nowPlaying?.file?.id, nowPlaying?.track);
  const currentTime = useHTMLPlayer
    ? HTMLPlayerCurrentTime
    : daw.getTrackTime(dawTrackId);
  return <>{formatDuration(asSeconds(currentTime))}</>;
};

export const GlobalAudioCurrentFormattedTime = () => {
  const { nowPlaying } = useGlobalAudioPlayer();
  const { useHTMLPlayer, currentDuration } = useHTMLAudioPlayer();
  const dawTrackId = getDAWTrackId(nowPlaying?.file?.id, nowPlaying?.track);
  const dawTrack = daw.getTrack(dawTrackId);
  const duration = useHTMLPlayer ? currentDuration : dawTrack?.duration || 0;

  return (
    <>
      <GlobalAudioCurrentTime />
      <span> / {formatDuration(asSeconds(duration))}</span>
    </>
  );
};

const localTrackPlayCache: Record<string, boolean> = {};

export const GlobalAudioPlayer = ({
  preview,
  onNext,
  onPrev,
  seekable = true,
  className,
}: {
  preview: React.ReactNode;
  onNext?: () => void;
  onPrev?: () => void;
  seekable?: boolean;
  className?: string;
}) => {
  useDAW(["isPlaying", "tracks", "volume", "isLoading"]);
  const { user } = useAuth();
  const { theme } = useTheme();
  const {
    nowPlaying,
    prevTrackId,
    nextTrackId,
    isActive,
    play,
    pause,
    goToTrack,
    fetchAudioFiles,
    setAudioQuality,
    seekTrack,
    skipAudioMode,
  } = useGlobalAudioPlayer();
  const {
    useHTMLPlayer,
    currentDuration,
    getTrack,
    isActive: isHTMLPlayerActive,
    isLoading,
    isPlaying,
    onTogglePlay,
    seekTrack: HTMLseekTrack,
  } = useHTMLAudioPlayer();
  const { entity: currentSpace, loading: currentSpaceLoading } = useSpaceRaw();

  const dawTrackId = getDAWTrackId(nowPlaying?.file?.id, nowPlaying?.track);
  const dawTrack = daw.getTrack(dawTrackId);
  const HTMLTrack = getTrack(nowPlaying?.id);

  const isAudioQualityLocked = useMemo(() => {
    if (!currentSpaceLoading && currentSpace) {
      return isSpaceAudioQualityLocked(currentSpace);
    }
    return false;
  }, [
    currentSpace?.subscriptionTier,
    currentSpace?.audioQualityLocked,
    currentSpaceLoading,
  ]);

  useEffect(() => {
    if (isAudioQualityLocked) {
      setAudioQuality(AUDIO_QUALITY.ORIGINAL, true, isAudioQualityLocked);
    } else {
      setAudioQuality(getPersistedAudioQuality(user));
    }
  }, [isAudioQualityLocked]);

  useEffect(() => {
    const trackId = nowPlaying?.track?.id;
    if (!trackId || !daw.state.isPlaying) return;
    const trackPlayId = `${trackId}:${getAuthId()}`;

    highnote.addTrackPlay({
      trackId,
      // Only trigger track plays the first time in a browser session
      sendNotification: !localTrackPlayCache[trackPlayId],
    });

    localTrackPlayCache[trackPlayId] = true;
  }, [daw.state.isPlaying, nowPlaying?.track?.id, user?.id]);

  useEffect(() => {
    const onKeyup = (e: KeyboardEvent) => {
      if (e.code !== "Space") return;
      if (["TEXTAREA", "INPUT"].includes((e.target as HTMLElement)?.tagName))
        return;
      e.preventDefault();
      if (useHTMLPlayer) {
        onTogglePlay();
        return;
      }
      if (daw.state.isPlaying) pause();
      else play();
    };
    // Focusing the play button must occur on keydown or another button may be focused briefly
    // between keydown and keyup
    const onKeydown = (e: KeyboardEvent) => {
      if (e.code !== "Space") return;
      if (["TEXTAREA", "INPUT"].includes((e.target as HTMLElement)?.tagName))
        return;
      e.preventDefault();
      // Focus the play button
      const playButton = document.getElementsByClassName(
        "highnote-audio-play-toggle play-pause",
      );
      if (playButton.length) {
        (playButton[0] as HTMLElement).focus();
      }
    };
    window.addEventListener("keyup", onKeyup);
    window.addEventListener("keydown", onKeydown);

    return () => {
      window.removeEventListener("keyup", onKeyup);
      window.removeEventListener("keydown", onKeydown);
    };
  }, [nowPlaying]);

  const prev = () => {
    if (skipAudioMode !== "full-skip") {
      useHTMLPlayer ? HTMLseekTrack("back") : seekTrack("back");
    } else {
      if (
        (!prevTrackId && !onPrev) ||
        daw.state.currentTime > dawTrack?.startTime + 1
      ) {
        goToTrack(nowPlaying.id, 0);
        return;
      }
      if (onPrev) {
        onPrev();
        return;
      }

      goToTrack(prevTrackId);
    }
  };

  const next = () => {
    if (skipAudioMode !== "full-skip") {
      useHTMLPlayer ? HTMLseekTrack("forward") : seekTrack("forward");
    } else {
      if (onNext) {
        onNext();
        return;
      }

      goToTrack(nextTrackId);
    }
  };

  const duration = useHTMLPlayer ? currentDuration : dawTrack?.duration || 0;
  const error =
    nowPlaying?.file?.processingErrorV3 ||
    (useHTMLPlayer ? HTMLTrack?.error : dawTrack?.error);
  const isPlayable = isFilePlayable(nowPlaying?.file);

  return (
    <div
      className={classNames("highnote-global-audio-player", className)}
      data-cypress-id="global-audio-player"
      data-is-active={useHTMLPlayer ? isHTMLPlayerActive : isActive}
      data-is-playable={
        useHTMLPlayer ? isLoading : dawTrack && !daw.state.isLoading
      }
      data-has-error={!!error}
      data-is-playing={useHTMLPlayer ? isPlaying : daw.state.isPlaying}
      data-theme={theme}
      data-is-empty={!nowPlaying}
    >
      <div className="content-top">{seekable && <GlobalAudioSeeker />}</div>
      <div className="content-bottom">
        <section className="left">
          {preview}
          {nowPlaying?.track && !isPlayable && (
            <AudioProcessingIcon
              file={nowPlaying?.file}
              onRetry={() => fetchAudioFiles(nowPlaying.track)}
            />
          )}
        </section>
        <section className="center">
          <div className="time current">
            {isPlayable && (
              <>
                <GlobalAudioCurrentTime />
                <span className="mobile-only">
                  {" "}
                  / {formatDuration(asSeconds(duration))}
                </span>
              </>
            )}
          </div>
          <BaseMediaControls
            isNextDisabled={!nextTrackId && !onNext}
            onPrevClick={prev}
            onNextClick={next}
          >
            <div className="toggle-play">
              <GlobalAudioIcon disabled={!isPlayable} useLoadProgress />
            </div>
          </BaseMediaControls>
          <div className="time duration">
            {isPlayable && formatDuration(asSeconds(duration))}
          </div>
        </section>
        <section className="right">
          <ToggleAudioQuality isAudioQualityLocked={isAudioQualityLocked} />
          <VolumeControl />
          <CustomIntercomLauncher
            size={BUTTON_SIZE.LARGE}
            className="global-audio-player-intercom"
          />
        </section>
      </div>
    </div>
  );
};

const LoadProgress = ({ type = "icon" }: { type?: "icon" | "button" }) => {
  const { nowPlaying } = useGlobalAudioPlayer();
  const dawTrack = daw.getTrack(nowPlaying.id);
  if (!dawTrack) return null;

  const loadProgress = dawTrack.loadProgress || 0;
  const loadRadius = 22;
  const loadCircumference = Math.PI * 2 * loadRadius;
  const offset = (1 - loadProgress) * loadCircumference;

  return (
    <div className="load-progress">
      <label>{Math.floor(loadProgress * 100)}%</label>
      {type === "icon" && (
        <svg>
          <circle
            cx="50%"
            cy="50%"
            r={loadRadius}
            strokeDashoffset={offset}
            strokeDasharray={loadCircumference}
          />
        </svg>
      )}
    </div>
  );
};

const useGlobalAudioButton = ({
  canUpdateQueueOnPlay,
}: {
  canUpdateQueueOnPlay?: boolean;
}) => {
  useDAW(["isPlaying", "isLoading", "tracks"]);
  const { pathname } = useLocation();
  const {
    nowPlaying,
    play,
    pause,
    setQueue,
    currentQueueOrder,
    nextQueueOrder,
  } = useGlobalAudioPlayer();
  const {
    useHTMLPlayer,
    playTrack,
    isLoading: isHTMLPlayerLoading,
    isPlaying: isHTMLPlayerPlaying,
    onTogglePlay: HTMLPlayerTogglePlay,
  } = useHTMLAudioPlayer();
  // Specific logic used for the Space header, to update the queue to the current page
  // when the play button is clicked.
  const isNowPlayingPreviousSpace = canUpdateQueueOnPlay
    ? pathname !== currentQueueOrder.pageId
    : false;
  const isLoading = useHTMLPlayer ? isHTMLPlayerLoading : daw.state.isLoading;

  // If the current page is not the same as the current queue order page, we want to
  // ensure that the button does not indicate that it is playing, as the current queue for the page.
  const isPlaying = isNowPlayingPreviousSpace
    ? false
    : useHTMLPlayer
      ? isHTMLPlayerPlaying
      : daw.state.isPlaying;
  const isPlayerReady = useHTMLPlayer
    ? !isHTMLPlayerLoading
    : !daw.state.isLoading && daw.state.tracks.length > 0;

  // If the current page is not the same as the current queue order page,
  // check if there are tracks in the next queue order to determine if the
  // play button should be enabled.
  const isPlayable = isNowPlayingPreviousSpace
    ? nextQueueOrder.queue.length > 0
    : !!nowPlaying.id && isPlayerReady;

  const onTogglePlay = () => {
    if (!isPlayable) return;
    if (canUpdateQueueOnPlay) {
      // We setQueue to update the current queue order to the current page
      // GlobalAudioIcon is used in the header, and GlobalAudioPlayer, so
      // we want to differentiate between the two. GlobalAudioPlayer should
      // not update the queue, as it is already set, where as the Space header
      // should update the queue to the current page.
      const nextTrackToPlayInQueue = nextQueueOrder.queue[0];
      setQueue({
        type: "UPDATE_CURRENT_FROM_NEXT",
        _tracks: [],
        currentPageId: pathname,
      });
      if (nextTrackToPlayInQueue) {
        if (useHTMLPlayer) {
          playTrack(nextTrackToPlayInQueue);
          return;
        }
        play(nextTrackToPlayInQueue);
        return;
      }
    }
    if (useHTMLPlayer) {
      HTMLPlayerTogglePlay();
      return;
    }
    if (daw.state.isPlaying) {
      pause();
      return;
    }
    play();
  };

  return {
    isPlayable,
    isPlaying,
    isLoading,
    onTogglePlay,
  };
};

export const GlobalAudioIcon = ({
  disabled,
  useLoadProgress,
  canUpdateQueueOnPlay,
  type = "icon",
  fullWidth,
}: {
  disabled?: boolean;
  useLoadProgress?: boolean;
  canUpdateQueueOnPlay?: boolean;
  type?: "icon" | "button";
  fullWidth?: boolean;
}) => {
  const { isLoading, isPlayable, isPlaying, onTogglePlay } =
    useGlobalAudioButton({
      canUpdateQueueOnPlay,
    });

  const showLoadProgress = useLoadProgress && isLoading;
  const isButtonDisabled =
    !isPlayable || isLoading || disabled || showLoadProgress;

  if (type === "button") {
    return (
      <div
        data-cypress-id="global-audio-icon"
        data-is-playable={Boolean(isPlayable)}
        data-is-playing={isPlaying}
        className={classNames({ ["full-width"]: fullWidth })}
      >
        <Button
          size={BUTTON_SIZE.MEDIUM}
          theme={BUTTON_THEME.PRIMARY_INVERSE}
          disabled={isButtonDisabled}
          onClick={onTogglePlay}
          fullWidth={fullWidth}
          startIcon={
            showLoadProgress ? null : isPlaying ? (
              <PauseFillSVG />
            ) : (
              <PlayFillSVG />
            )
          }
        >
          {showLoadProgress ? (
            <LoadProgress type={type} />
          ) : (
            <>{isPlaying ? "Pause" : "Play"}</>
          )}
        </Button>
      </div>
    );
  }

  return (
    <AudioPlayToggle
      wrapperClassName="highnote-global-audio-icon"
      cypressId="global-audio-icon"
      isPlayable={isPlayable}
      isPlaying={isPlaying}
      disabled={isButtonDisabled}
      isLoading={daw.state.isLoading}
      renderToggleButton={!useLoadProgress || !daw.state.isLoading}
      onTogglePlay={onTogglePlay}
    >
      {useLoadProgress && isLoading && <LoadProgress />}
    </AudioPlayToggle>
  );
};

export const TrackAudioIcon = ({
  dawTrackId,
  fallback,
  trackVersionId,
}: {
  dawTrackId?: string;
  fallback?: React.ReactNode;
  trackVersionId?: string;
}) => {
  const { isMobile } = useViewport();
  const { fetchAudioFiles } = useGlobalAudioPlayer();
  const {
    handleOnTogglePlay,
    isNowLoading,
    isNowPlaying,
    isPlayable,
    isPlaying,
    isQueueable,
    queueItem,
  } = useTrackRow(dawTrackId, trackVersionId);

  if (!isQueueable || !isFilePlayable(queueItem.file)) {
    if (isQueueable) {
      return (
        <AudioProcessingIcon
          file={queueItem.file}
          dawTrackId={dawTrackId}
          onRetry={() => fetchAudioFiles(queueItem.track)}
        />
      );
    }
    return (
      <StatusIndicator
        isLoading
        errorMessage={queueItem?.file?.processingErrorV3}
      />
    );
  }

  return (
    <AudioPlayToggle
      wrapperClassName="highnote-track-audio-icon"
      isNowPlaying={Boolean(isNowPlaying)}
      isPlayable={isPlayable}
      isPlaying={isPlaying}
      renderToggleButton={isQueueable && isFilePlayable(queueItem.file)}
      isLoading={isNowLoading}
      pauseIcon={<PauseFillSVG />}
      playIcon={<PlayFillSVG />}
      onTogglePlay={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        // e.stopPropagation() is used to prevent the track row from being clicked
        // upon clicking the play button. To avoid any unintended behavior and bubbling
        // of events.
        e.stopPropagation();
        handleOnTogglePlay();
      }}
      asDiv={isMobile}
    >
      {isQueueable && !isFilePlayable(queueItem.file) && (
        <AudioProcessingIcon
          file={queueItem.file}
          dawTrackId={dawTrackId}
          onRetry={() => fetchAudioFiles(queueItem.track)}
        />
      )}
      {fallback && <span className="fallback">{fallback}</span>}
    </AudioPlayToggle>
  );
};

export const useTrackRow = (dawTrackId: string, trackVersionId?: string) => {
  useDAW(["isPlaying", "isLoading"]);
  const { pathname } = useLocation();
  const {
    nowPlaying,
    getQueueItem,
    play,
    pause,
    setQueue,
    currentQueueOrder,
    queueTrackVersion,
  } = useGlobalAudioPlayer();
  const {
    useHTMLPlayer,
    isLoading,
    isPlaying: isHTMLPlayerPlaying,
    playTrack,
    onTogglePlay,
  } = useHTMLAudioPlayer();

  const queueItem = getQueueItem(dawTrackId, trackVersionId);

  const isQueueable = queueItem && queueItem.file;
  const isNowPlaying = isQueueable && nowPlaying?.id === dawTrackId;
  const isPlaying =
    queueItem.file?.id === nowPlaying.file?.id &&
    isNowPlaying &&
    (useHTMLPlayer ? isHTMLPlayerPlaying : daw.state.isPlaying);
  const isPlayable = isQueueable && isFilePlayable(queueItem.file);
  const isNowLoading =
    (useHTMLPlayer ? isLoading : daw.state.isLoading) && isNowPlaying;

  const handleOnTogglePlay = () => {
    if (trackVersionId) {
      // Set queue to only the track, to differentiate from the main
      // top level queue of the Space page. Playing a track version
      // should only be a temporary queue, and only play that track version once.
      setQueue({
        type: "SET_VERSION",
        _tracks: [queueItem.track],
        currentPageId: pathname,
      });
      queueTrackVersion(dawTrackId, trackVersionId);
      // TODO: daw.__minDuration is not working as expected
      daw.__minDuration = queueItem.file.metadata.duration;
      play(dawTrackId);
      return;
    }
    if (pathname !== currentQueueOrder.pageId) {
      setQueue({
        type: "UPDATE_CURRENT_FROM_NEXT",
        _tracks: [],
        currentPageId: pathname,
      });
    }
    if (useHTMLPlayer) {
      if (nowPlaying?.id === dawTrackId) {
        onTogglePlay();
        return;
      }
      playTrack(dawTrackId);
      return;
    }
    if (!isPlayable || isNowLoading) return;

    if (isNowPlaying) {
      if (daw.state.isPlaying) pause();
      else play();
      return;
    }

    play(dawTrackId);
  };

  return {
    isQueueable,
    isNowPlaying,
    isPlaying,
    isPlayable,
    isNowLoading,
    queueItem,
    handleOnTogglePlay,
  };
};
